import { Loader } from '@/features/common';
import { useWorkspacePermission } from '@/features/permissions';
import { useWorkspaceId } from '@/hooks/router';
import { getCustomerQueryKey } from '@/queries/subscriptions/keys';
import { usePostSetupIntentMutation } from '@/queries/subscriptions/mutations';
import { useGetCustomer } from '@/queries/subscriptions/queries';
import { queryClient } from '@/utils/reactQuery';
import styled from '@emotion/styled';
import { DSButton } from '@hundred5/design-system';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useHandleBillingError } from '../../hooks';
import { Disclaimer, StripeElement } from './ui';

interface BillingDetailsPaymentEditFormProps {
  onSuccess?: () => void;
}

export const BillingDetailsPaymentEditForm = ({
  onSuccess,
}: BillingDetailsPaymentEditFormProps) => {
  const [clientSecret, setClientSecret] = useState('');
  const workspaceId = useWorkspaceId();
  const { handleUnknownError } = useHandleBillingError();
  const { data: customer, isPending: pendingCustomer } = useGetCustomer(
    Number(workspaceId)
  );

  const { mutate: setupIntentMutate, isPending: pendingSetupIntent } =
    usePostSetupIntentMutation({
      onSuccess: (data) => {
        setClientSecret(data.client_secret);
      },
      onError: (error) => {
        handleUnknownError('Failed to create setup intent', error);
      },
    });

  useEffect(() => {
    if (customer) setupIntentMutate({ workspace_id: Number(workspaceId) });
  }, [customer, workspaceId, setupIntentMutate]);

  if (pendingCustomer || pendingSetupIntent) {
    return <Loader />;
  }

  if (!customer || !clientSecret) {
    return null;
  }

  return (
    <StripeElement clientSecret={clientSecret} site={customer.site}>
      <BillingDetailsPaymentEditFormInner
        onSuccess={onSuccess}
        site={customer.site}
      />
    </StripeElement>
  );
};

interface BillingDetailsPaymentEditFormInnerProps {
  site: string;
  onSuccess?: () => void;
}

function BillingDetailsPaymentEditFormInner({
  site,
  onSuccess,
}: BillingDetailsPaymentEditFormInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const workspaceId = useWorkspaceId();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canEditBillingInfo = useWorkspacePermission()('billing info', 'update');
  const { handleStripeError } = useHandleBillingError();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });
    setIsSubmitting(false);

    if (result.error) {
      if (!['card_error', 'validation_error'].includes(result.error.type)) {
        handleStripeError(result.error);
      }
    } else {
      queryClient.invalidateQueries({
        queryKey: getCustomerQueryKey(Number(workspaceId)),
      });
      onSuccess?.();
    }
  };

  return (
    <StripeForm onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: {
            type: 'tabs',
            defaultCollapsed: false,
          },
          terms: {
            card: 'never',
          },
        }}
        onReady={() => setShowDisclaimer(true)}
      />

      {showDisclaimer && <Disclaimer site={site} />}

      <Footer>
        <DSButton
          variant="primary-purple"
          type="submit"
          disabled={!canEditBillingInfo || isSubmitting}
          data-rh={!canEditBillingInfo ? 'Only owners can edit details' : null}
        >
          {isSubmitting ? 'Saving...' : 'Save changes'}
        </DSButton>
      </Footer>
    </StripeForm>
  );
}

const StripeForm = styled.form`
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Footer = styled.footer`
  padding-top: 36px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
`;
